import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firebaseConfig } from '../environments/environment';
import { Account } from 'src/models/account.model';

@Injectable({
  providedIn: 'root'
})
export class TenantPublicService {

  private firebaseProjectId: string;

  constructor(private http: HttpClient) {
    // Extract project ID from Firebase config
    this.firebaseProjectId = firebaseConfig.projectId;
  }

  /**
   * Fetch account by account_key using Firebase REST API
   * Try multiple approaches: settings collection first, then accounts collection
   */
  async getAccountByKeyPublic(accountKey: string): Promise<Account | null> {
    // Try fetching from settings collection first (document ID = account_key)
    // This might have more permissive rules
    try {
      const settingsUrl = `https://firestore.googleapis.com/v1/projects/${this.firebaseProjectId}/databases/(default)/documents/settings/${accountKey}`;
      const settingsResponse = await this.http.get<any>(settingsUrl).toPromise();

      if (settingsResponse && settingsResponse.fields) {
        console.log('Loaded from settings collection');
        const account: any = {
          account_key: accountKey,
          account_id: accountKey
        };

        // Settings doc has { settings: { tenants: [...], ... } }
        if (settingsResponse.fields.settings) {
          account.settings = this.parseFirestoreValue(settingsResponse.fields.settings);
        }

        // Also get account_name if available
        if (settingsResponse.fields.account_name) {
          account.account_name = this.parseFirestoreValue(settingsResponse.fields.account_name);
        }

        return account as Account;
      }
    } catch (settingsError) {
      console.log('Settings collection not accessible, trying accounts collection...');
    }

    // Fallback: Try accounts collection directly by document ID
    try {
      const accountsUrl = `https://firestore.googleapis.com/v1/projects/${this.firebaseProjectId}/databases/(default)/documents/accounts/${accountKey}`;
      const accountResponse = await this.http.get<any>(accountsUrl).toPromise();

      if (accountResponse && accountResponse.fields) {
        console.log('Loaded from accounts collection by ID');
        return this.parseFirestoreDocument(accountResponse);
      }
    } catch (accountError) {
      console.log('Accounts collection by ID not accessible, trying query...');
    }

    // Last resort: Query accounts collection
    try {
      const queryUrl = `https://firestore.googleapis.com/v1/projects/${this.firebaseProjectId}/databases/(default)/documents:runQuery`;

      const query = {
        structuredQuery: {
          from: [{ collectionId: 'accounts' }],
          where: {
            fieldFilter: {
              field: { fieldPath: 'account_key' },
              op: 'EQUAL',
              value: { stringValue: accountKey }
            }
          },
          limit: 1
        }
      };

      const response = await this.http.post<any[]>(queryUrl, query).toPromise();

      if (response && response.length > 0 && response[0].document) {
        console.log('Loaded from accounts collection via query');
        return this.parseFirestoreDocument(response[0].document);
      }
    } catch (queryError) {
      console.error('All fetch methods failed:', queryError);
      throw new Error('Unable to load tenant portal. Please contact your administrator to configure public access.');
    }

    return null;
  }

  /**
   * Parse Firestore document format to plain object
   */
  private parseFirestoreDocument(doc: any): Account {
    const fields = doc.fields;
    const account: any = {};

    // Extract document ID from the name path
    const nameParts = doc.name.split('/');
    account.account_id = nameParts[nameParts.length - 1];

    for (const key in fields) {
      account[key] = this.parseFirestoreValue(fields[key]);
    }

    return account as Account;
  }

  /**
   * Parse Firestore value types to JavaScript types
   */
  private parseFirestoreValue(value: any): any {
    if (value.stringValue !== undefined) return value.stringValue;
    if (value.integerValue !== undefined) return parseInt(value.integerValue, 10);
    if (value.doubleValue !== undefined) return value.doubleValue;
    if (value.booleanValue !== undefined) return value.booleanValue;
    if (value.timestampValue !== undefined) return new Date(value.timestampValue);
    if (value.nullValue !== undefined) return null;

    if (value.arrayValue) {
      return (value.arrayValue.values || []).map((v: any) => this.parseFirestoreValue(v));
    }

    if (value.mapValue) {
      const obj: any = {};
      const mapFields = value.mapValue.fields || {};
      for (const k in mapFields) {
        obj[k] = this.parseFirestoreValue(mapFields[k]);
      }
      return obj;
    }

    return value;
  }
}
